import { useEffect, useState } from 'react'
import axios from '../axios'
import { Card, Col, Row, Spin, Typography } from 'antd';
import '../styles/HomePage.css';
// Add Chart.js and react-chartjs-2 imports
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title as ChartTitle,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
// Import zoom plugin
import zoomPlugin from 'chartjs-plugin-zoom';

// Register Chart.js components and plugins
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ChartTitle,
    Tooltip,
    Legend,
    zoomPlugin // Register the zoom plugin
);

const { Title, Paragraph } = Typography;

function HomePage() {
    const [server, setServer] = useState(() => {
        const server = localStorage.getItem('selectedServer');
        return server ? JSON.parse(server) : null;
    });
    const [players, setPlayers] = useState([]);
    const [huges, setHuges] = useState([]);
    const [hugeCount, setHugeCount] = useState(0);
    const [titanicCount, setTitanicCount] = useState(0);
    const [gargantuanCount, setGargantuanCount] = useState(0);
    const [hatchesToday, setHatchesToday] = useState(0);
    const [totalRAP, setTotalRAP] = useState(0);
    const [BGRap, setBGRap] = useState([]);
    const [loading, setLoading] = useState(true);
    // New state variables for the hatch history chart
    const [hatchHistoryData, setHatchHistoryData] = useState([]);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });

    useEffect(() => {
        async function init() {
            await axios.get(`/api/user/player/${server?.id}`)
                .then((res) => {
                    setPlayers(res.data);
                })
                .catch((err) => {
                    console.log(err);
                });

            await axios.get(`/api/user/inventory/${server?.id}`)
                .then((res) => {
                    setHuges(res.data);
                })
                .catch((err) => {
                    console.log(err);
                });

            await axios.get(`/api/user/hatches/last24hours/${server?.id}`)
                .then(async (res) => {
                    var hatches = res.data.hatches;
                    setHatchesToday(hatches);
                    setLoading(false);
                })
                .catch((err) => {
                    console.log(err);
                    window.location.reload();
                });

            axios.get(`/api/user/hatchHistory/${server?.id}`)
                .then(async (res) => {
                    setHatchHistoryData(res.data);

                    // Process data for the chart
                    const processedData = processHatchHistoryData(res.data);
                    setChartData({
                        labels: processedData.labels,
                        datasets: [
                            {
                                label: 'Huges Hatched',
                                data: processedData.counts,
                                borderColor: '#b48ee0',
                                backgroundColor: 'rgba(180, 142, 224, 0.14)',
                                pointBackgroundColor: '#d9b8ff',
                                pointBorderColor: '#d9b8ff',
                                borderWidth: 2,
                                tension: 0.3,
                                fill: true,
                            },
                        ],
                    });
                })
                .catch((err) => {
                    console.log(err);
                    window.location.reload();
                });

            await fetch("https://ps99.biggamesapi.io/api/rap", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                }
            }).then(res => res.json()).then(data => {
                setBGRap(data.data);
            })
        }

        init();
    }, []);

    useEffect(() => {
        if (players.length > 0) {
            var tempHugeCount = 0;
            var tempTitanicCount = 0;
            var tempGargantuanCount = 0;
            for (var huge of huges) {
                if (huge.name.includes("Huge")) tempHugeCount += huge.quantity;
                if (huge.name.includes("Titanic")) tempTitanicCount += huge.quantity;
                if (huge.name.includes("Gargantuan")) tempGargantuanCount += huge.quantity;
            }
            setHugeCount(tempHugeCount);
            setTitanicCount(tempTitanicCount);
            setGargantuanCount(tempGargantuanCount);
        }
    }, [huges]);

    useEffect(() => {
        if (huges.length > 0 && BGRap.length > 0) {
            var tempRapItems = [];
            var tempTotalRAP = 0;
            for (var item of huges) {
                var configData = {
                    id: item.name
                }
                if (item.variantType === "GOLDEN") configData.pt = 1;
                if (item.variantType === "RAINBOW") configData.pt = 2;
                if (item.variantType === "SHINY") configData.sh = true;
                if (item.variantType === "SHINY_GOLDEN") {
                    configData.pt = 1;
                    configData.sh = true;
                }
                if (item.variantType === "SHINY_RAINBOW") {
                    configData.pt = 2;
                    configData.sh = true;
                }
                var rapItem = BGRap.find(rap => JSON.stringify(rap.configData) === JSON.stringify(configData));
                if (rapItem) {
                    item.rap = rapItem.value;
                    tempRapItems.push(item);
                }
            }
            for (var _item of tempRapItems) {
                tempTotalRAP += _item.rap * _item.quantity;
            }
            var formattedRap
            if (tempTotalRAP >= 1e12) {
                formattedRap = (tempTotalRAP / 1e12).toFixed(2) + ' Trillion';
            } else if (tempTotalRAP >= 1e9) {
                formattedRap = (tempTotalRAP / 1e9).toFixed(2) + ' Billion';
            } else if (tempTotalRAP >= 1e6) {
                formattedRap = (tempTotalRAP / 1e6).toFixed(2) + ' Million';
            } else {
                formattedRap = tempTotalRAP.toString();
            }
            setTotalRAP(formattedRap);
        }
    }, [huges, BGRap]);

    // Function to process hatch history data for the chart
    const processHatchHistoryData = (data) => {
        // Extract all hatch entries
        const allHatches = data.flatMap(player => player.hatchHistory || []);

        // Sort by timestamp (oldest first)
        allHatches.sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp));

        // Group hatches by day
        const hatchesByDay = {};
        allHatches.forEach(hatch => {
            const date = new Date(parseInt(hatch.timestamp) * 1000);
            const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD

            if (!hatchesByDay[dateStr]) {
                hatchesByDay[dateStr] = 0;
            }
            hatchesByDay[dateStr]++;
        });

        // Generate chart data
        const labels = Object.keys(hatchesByDay).sort();
        const counts = labels.map(date => hatchesByDay[date]);

        return { labels, counts };
    };

    return (
        <div className='home-page-div'>
            {loading ? (
                <div className='loading-div'>
                    {/* <Title level={3}>Loading your dashboard...</Title> */}
                    <Spin size="large" />
                </div>
            ) : (
                <div className="dashboard-container">
                    <Title level={3} className="section-title">Server Overview</Title>

                    <div className="hero-stat">
                        <span className="hero-stat-label">Total Exclusive Value (RAP)</span>
                        <span className="hero-stat-value">{totalRAP}</span>
                    </div>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Card className="stat-card" bordered={false}>
                                <span className="stat-card-label">Players</span>
                                <div className="stat-value">{players.length}</div>
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={6}>
                            <Card className="stat-card" bordered={false}>
                                <span className="stat-card-label">Huge</span>
                                <div className="stat-value">{hugeCount}</div>
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={6}>
                            <Card className="stat-card" bordered={false}>
                                <span className="stat-card-label">Titanic</span>
                                <div className="stat-value">{titanicCount}</div>
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={6}>
                            <Card className="stat-card" bordered={false}>
                                <span className="stat-card-label">Gargantuan</span>
                                <div className="stat-value">{gargantuanCount}</div>
                            </Card>
                        </Col>

                        <Col xs={24}>
                            <Card className="stat-card" bordered={false}>
                                <span className="stat-card-label">Huges hatched, last 24h</span>
                                <div className="stat-value">{hatchesToday}</div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Add Hatch History Graph */}
                    <div className="chart-section">
                        <Title level={3} className="section-title">Huge Hatch History</Title>
                        <div className="chart-container">
                            {chartData.labels.length > 0 ? (
                                <Line
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            x: {
                                                ticks: { color: '#a294b8' },
                                                grid: { color: 'rgba(162, 148, 184, 0.15)' },
                                            },
                                            y: {
                                                ticks: { color: '#a294b8' },
                                                grid: { color: 'rgba(162, 148, 184, 0.15)' },
                                            },
                                        },
                                        plugins: {
                                            legend: {
                                                position: 'top',
                                                labels: { color: '#a294b8' },
                                            },
                                            title: {
                                                display: true,
                                                text: 'Huge Pet Hatches Over Time',
                                                color: '#a294b8',
                                            },
                                            tooltip: {
                                                mode: 'index',
                                                intersect: false,
                                                backgroundColor: '#1c1029',
                                                titleColor: '#f0e9f7',
                                                bodyColor: '#f0e9f7',
                                                borderColor: '#3a2456',
                                                borderWidth: 1,
                                            },
                                            zoom: {
                                                pan: {
                                                    enabled: true,
                                                    mode: 'x',
                                                    modifierKey: null, // Allow panning without modifier key
                                                },
                                                zoom: {
                                                    wheel: {
                                                        enabled: true,
                                                    },
                                                    pinch: {
                                                        enabled: true,
                                                    },
                                                    mode: 'x',
                                                    drag: {
                                                        enabled: false, // Disable drag selection for zooming
                                                    },
                                                }
                                            }
                                        },
                                        hover: {
                                            mode: 'index',
                                            intersect: false,
                                        },
                                    }}
                                    height={300}
                                />
                            ) : (
                                <div className="no-data">
                                    <Paragraph>No hatch history data available</Paragraph>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default HomePage;